<template>
  <div class="p-4 glass-frosted h-full flex flex-col rounded-8xl">
    <p class="text-black-white text-l-medium mb-3">{{ $t('parameters.liftoffHeight.title') }}</p>

    <div class="flex-1 flex mt-1.5 gap-1">
      <div class="h-full pb-1 flex flex-col justify-between">
        <Button
          color="text-b-w"
          variant="frameless"
          shape="square"
          size="icon-xs"
          @click="handleStepUp"
        >
          <Icon icon="Direction-Wireframe-Up2-1" />
        </Button>
        <Button
          color="text-b-w"
          variant="frameless"
          shape="square"
          size="icon-xs"
          @click="handleStepDown"
        >
          <Icon icon="Direction-Wireframe-Down2" />
        </Button>
      </div>
      <div class="relative">
        <Slider
          v-model="sliderValue"
          :min="scaleMin"
          :max="scaleMax"
          :step="step"
          direction="vertical"
          thumb-alignment="overflow"
          :thumb-size="THUMB_HEIGHT"
          thumb-cross-size="100%"
          class="h-52 w-32"
          track-class="w-full rounded-3xl bg-linear-[var(--color-rt1)]"
          :range-class="['bg-transparent', positionTransitionClass]"
          :thumb-class="['bg-transparent rounded-none', positionTransitionClass]"
          @pointerdown.capture="hasInteracted = true"
          @value-commit="commitDeadZone"
        >
          <template #thumb>
            <div class="relative flex justify-center">
              <Icon icon="Direction-FIll-SwipeUD1" class="w-6 h-6 text-black-white" />
              <div class="absolute w-full h-px bg-black-white top-[50%]" />

              <div
                class="absolute -right-14 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 pointer-events-none text-center leading-none"
              >
                <span class="text-2xl-semibold text-black-white font-[Barlow] text-left">
                  {{ displayValue.toFixed(1) }}
                </span>
              </div>
            </div>
          </template>
        </Slider>

        <div
          class="absolute top-1 left-2 right-1 text-s-medium text-blue-dark-default pointer-events-none"
        >
          {{ scaleMax.toFixed(1) }}
        </div>
        <div
          class="absolute bottom-2 left-2 right-1 text-s-medium text-brand-pink pointer-events-none"
        >
          {{ scaleMin.toFixed(1) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { Button, Icon, Slider } from '@rapoo/element'
  import type { ModuleSize } from '../utils/types'
  import type { ParametersContext } from '../../composables/useParameters'
  import { useLatestCommit } from '@/composables/useSliderDrag'

  const props = defineProps<{
    parameters: ParametersContext
    size?: ModuleSize
  }>()

  const THUMB_HEIGHT = 24

  const ctx = props.parameters
  const currentValue = computed(() => ctx.state.liftoffHeight.value)
  const scaleMin = computed(() => ctx.state.liftoffHeightScale.value[0])
  const scaleMax = computed(
    () => ctx.state.liftoffHeightScale.value[ctx.state.liftoffHeightScale.value.length - 1],
  )
  const step = computed(() => {
    const scale = ctx.state.liftoffHeightScale.value
    if (scale.length < 2) return 0.1
    return parseFloat((scale[1] - scale[0]).toFixed(2))
  })

  // Slider 需要数组形式的 v-model
  const sliderValue = ref([currentValue.value])
  const pendingHeight = ref<number | null>(null)
  const hasInteracted = ref(false)
  const positionTransitionClass = computed(() =>
    hasInteracted.value && !ctx.state.loading.value
      ? 'transition-[left,right,top,bottom] duration-150 ease-out motion-reduce:transition-none'
      : '',
  )

  const { commit: commitHeightWrite } = useLatestCommit(async (height: number) => {
    await ctx.actions.setLiftoffHeight(height)
    if (pendingHeight.value !== height) return
    pendingHeight.value = null
    sliderValue.value = [ctx.state.liftoffHeight.value]
  })

  function displayedHeight() {
    return pendingHeight.value ?? sliderValue.value[0] ?? currentValue.value
  }

  function commitHeight(height: number) {
    hasInteracted.value = true
    pendingHeight.value = height
    sliderValue.value = [height]
    commitHeightWrite(height)
  }

  /** 拖动过程中实时显示的值 */
  const displayValue = computed(() => sliderValue.value[0])

  watch(currentValue, (val) => {
    if (pendingHeight.value !== null) return
    sliderValue.value = [val]
  })

  const handleStepUp = () => {
    commitHeight(displayedHeight() + step.value)
  }

  const handleStepDown = () => {
    commitHeight(displayedHeight() - step.value)
  }

  const commitDeadZone = (val: number[]) => {
    commitHeight(val[0])
  }
</script>
